import { z } from 'zod';
import { QueryStatsSchema } from './query-stats.js';
import { CommentaryRunMetaSchema } from './commentary-run-meta.js';
import { RichResponseMetadataSchema } from './rich-response.js';
import { InquiryStageSchema } from './inquiry-stage.js';
import { ScriptureResponseSchema } from './scripture-response.js';
import { VariantAnalysisSchema, PericopeSchema } from './chat-response.js';
import { SearchResultSchema } from '../search/search-result.js';
import { LocatorResultSchema } from './locator-result.js';
/**
 * SSE events emitted during the study inquiry pipeline.
 */
export const ChatStepEventSchema = z.object({
    type: z.literal('step'),
    step: InquiryStageSchema,
    status: z.enum(['running', 'done']),
    durationMs: z.number().optional(),
});
export const ChatCompleteEventSchema = z.object({
    type: z.literal('complete'),
    // v2 ChatResponse fields
    itIsWritten: z.string().optional(),
    reference: z.string().optional(),
    mlResponse: z.string().optional(),
    /** Raw model body — see PipelineResponseSchema.rawModelBody. */
    rawModelBody: z.string().optional(),
    /** The prompt sent to the model — see PipelineResponseSchema.prompt. */
    prompt: z.string().optional(),
    strongsAnalysis: z.string().nullable().optional(),
    textualVariant: VariantAnalysisSchema.nullable().optional(),
    crossReferences: z.array(z.string()).optional(),
    pericope: PericopeSchema.nullable().optional(),
    // v1 backward compat fields
    response: z.string().optional(),
    commentary: z.string().optional(),
    metadata: RichResponseMetadataSchema.optional(),
    intent: z.object({
        intent: z.string(),
        confidence: z.number(),
        requiresCommentary: z.boolean().optional(),
        requiresVariantData: z.boolean().optional(),
    }).optional(),
    analysis: z.unknown().optional(),
    sources: z.object({
        verseCount: z.number(),
        commentaryCount: z.number(),
        commentarySources: z.array(z.string()).optional(),
    }).optional(),
    stats: QueryStatsSchema.optional(),
    /** What the commentary run actually did — timings, LLMs called, guard and
     *  validator verdicts. Rides into the report's Metadata block. */
    runMeta: CommentaryRunMetaSchema.optional(),
    /** V6 edit theater — the verification receipt, persisted on the final
     *  message (also emitted live as a `receipt` event during the show). */
    editReceipt: z.array(z.object({
        kind: z.enum(['verified', 'corrected', 'confirmed']),
        label: z.string(),
    })).optional(),
});
export const ChatErrorEventSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
});
/** A generation delta — the client appends `text` to its live draft.
 *  The draft is presentation only; the `complete` event's body is ground
 *  truth and always replaces it (V6 token streaming). */
export const ChatTokenEventSchema = z.object({
    type: z.literal('token'),
    text: z.string(),
});
/** Restart the live draft (a judge re-roll discarded the streamed round). */
export const ChatTokenResetEventSchema = z.object({
    type: z.literal('token-reset'),
    reason: z.string().optional(),
});
/** Curtain-up for the edit theater: the verification pass is about to
 *  perform. `text` is the server's baseline body — the client silently
 *  reconciles its draft to it before animating ops (drift guardrail). */
export const EditStartEventSchema = z.object({
    type: z.literal('edit-start'),
    text: z.string(),
});
/** A check chip — one deterministic verification step starting/finishing.
 *  `skipped` marks an accuracy step that had nothing to do for this body —
 *  the checklist shows it rather than silently omitting it. */
export const CheckEventSchema = z.object({
    type: z.literal('check'),
    act: z.string(),
    label: z.string(),
    status: z.enum(['running', 'done', 'skipped']),
});
/** A semantic patch op the client animates against its live draft.
 *  Offsets refer to the draft state after all previous ops applied.
 *  `act` ties the op to its checklist step so the client can pace the
 *  check-offs in sync with the performance. */
export const EditOpEventSchema = z.object({
    type: z.literal('edit'),
    op: z.enum(['highlight', 'delete', 'type', 'replace', 'verify']),
    act: z.string().optional(),
    start: z.number(),
    end: z.number().optional(),
    text: z.string().optional(),
    reason: z.string().optional(),
});
export const ReceiptItemSchema = z.object({
    kind: z.enum(['verified', 'corrected', 'confirmed']),
    label: z.string(),
});
/** The verification receipt — the check chips collapse into this
 *  persistent summary at the end of the show. */
export const ReceiptEventSchema = z.object({
    type: z.literal('receipt'),
    items: z.array(ReceiptItemSchema),
});
export const InferenceProgressEventSchema = z.object({
    type: z.literal('inference-progress'),
    tokenCount: z.number(),
    elapsedMs: z.number(),
});
export const InferenceMetaEventSchema = z.object({
    type: z.literal('inference-meta'),
    hardware: z.object({
        hostname: z.string(),
        cpuModel: z.string().optional(),
        cpuCores: z.number().optional(),
        totalMemoryGb: z.number().optional(),
        freeMemoryGb: z.number().optional(),
        platform: z.string().optional(),
        arch: z.string().optional(),
        ollamaHost: z.string(),
    }),
    model: z.string(),
    maxTokens: z.number(),
});
export const ContentChunkEventSchema = z.object({
    type: z.literal('content-chunk'),
    section: z.enum(['scripture', 'strongs', 'cross-refs', 'commentary']),
    content: z.string(),
    reference: z.string().optional(),
    isFinal: z.boolean().default(false),
});
export const ScriptureResponseEventSchema = z.object({
    type: z.literal('scripture-response'),
    data: ScriptureResponseSchema,
    intent: z.object({
        intent: z.string(),
        confidence: z.number(),
    }),
    durationMs: z.number().optional(),
});
export const SearchResultsEventSchema = z.object({
    type: z.literal('search-results'),
    query: z.string(),
    bookName: z.string(),
    results: z.array(SearchResultSchema),
    total: z.number(),
    durationMs: z.number().optional(),
});
/** Shape 2 locator answer — verified verse citations, no commentary.
 *  Terminal event: the client renders the results and the stream ends. */
export const LocatorResultsEventSchema = z.object({
    type: z.literal('locator-results'),
    query: z.string(),
    entityName: z.string().nullable(),
    results: z.array(LocatorResultSchema),
    durationMs: z.number().optional(),
});
/** Admission-queue status for the single-GPU inference slot. Emitted only
 *  when the request actually waits — an immediately admitted request never
 *  sees a queue event, so the client's queue UI stays hidden by absence. */
export const QueueEventSchema = z.object({
    type: z.literal('queue'),
    state: z.enum(['waiting', 'admitted', 'full']),
    /** 1-based place among waiting seekers (1 = next up). 0 for admitted/full. */
    position: z.number(),
    /** Total seekers currently waiting (including this one). */
    depth: z.number(),
    /** Rolling-average estimate to admission; refreshed on every event. */
    estimatedWaitMs: z.number().optional(),
});
export const ChatEventSchema = z.discriminatedUnion('type', [
    ChatStepEventSchema,
    ChatCompleteEventSchema,
    ChatErrorEventSchema,
    ChatTokenEventSchema,
    ChatTokenResetEventSchema,
    EditStartEventSchema,
    CheckEventSchema,
    EditOpEventSchema,
    ReceiptEventSchema,
    InferenceProgressEventSchema,
    InferenceMetaEventSchema,
    ContentChunkEventSchema,
    ScriptureResponseEventSchema,
    SearchResultsEventSchema,
    LocatorResultsEventSchema,
    QueueEventSchema,
]);
//# sourceMappingURL=chat-events.js.map